// neomem
// the main neomem class

import { decorate, observable, action } from 'mobx'

import { IPlugin, Auth, User, UserId, Cache, INode, NodeId, NodeValue, Pattern, Props } from '../types'
import { Datastore } from '../datastores/types'
import { Node } from './node'
import { ontology as neomemOntology } from '../data/ontology'


export class NeomemBase {

  //----------------------------------------------------------------------
  // init
  //----------------------------------------------------------------------

  initializing = true
  hydrating = false
  user: User | undefined
  auth: Auth

  // plugins: IPlugin[] = []
  // plugins: Map<string, IPlugin> = new Map()
  plugins: {
    [index: string]: IPlugin,
  } = {}

  // subscribers: Subscriber[] = []
  cloud: Datastore = {} as Datastore // if don't set a value here, get an error in the constructor - why?
  local: Datastore = {} as Datastore // if don't set a value here, get an error in the constructor - why?
  db: Datastore = {} as Datastore // if don't set a value here, get an error in the constructor - why?

  cache: Cache = {} // the dictionary of nodes, indexed by id - a cache between db and ui
  // cache: Cache = new Map() // the dictionary of nodes, indexed by id - a cache between db and ui

  currentNodeId: NodeId | undefined
  currentUserId: NodeId | undefined //. just here for mobx?


  constructor(cloud: Datastore, local: Datastore, auth: Auth) {
    this.cloud = cloud
    this.local = local
    this.auth = auth
    // db points to either cloud or local //. not the greatest solution - better way?
    this.db = local // start with localstoragedb
  }


  //----------------------------------------------------------------------
  // plugins
  //----------------------------------------------------------------------

  addPlugin(plugin: IPlugin) {
    plugin.initialize()
    this.plugins[plugin.name] = plugin
  }


  //----------------------------------------------------------------------
  // ui
  //----------------------------------------------------------------------

  setInitializing(initializing: boolean) {
    this.initializing = initializing
  }

  setCurrentNodeId(nodeId: NodeId) {
    this.currentNodeId = nodeId
  }


  //----------------------------------------------------------------------
  // user
  //----------------------------------------------------------------------

  //. this is so so hacky - keeping copy of userid in app to have an observable,
  // while also exists down in the db ughh
  //. shouldn't use this in ui - use the value directly, as it's observable, eh?
  getCurrentUserId() {
    const currentUserId = this.db.getCurrentUserId()
    if (currentUserId !== this.currentUserId) throw new Error('mismatched userids')
    return currentUserId
  }
  setCurrentUserId(userId?: UserId) {
    this.currentUserId = userId
    if (this.db) {
      this.db.setCurrentUserId(userId)
    }
  }

  async createUser(user: User): Promise<User | undefined> {
    if (this.db) {
      return await this.db.createUser(user)
    }
    return undefined
  }

  async getUser(userId: UserId): Promise<User | undefined> {
    if (this.db) {
      return await this.db.getUser(userId) // can be undefined
    }
    return undefined
  }

  // get user record from database, or undefined if not there
  //. a bit funky to do this way
  async getCloudUser(userId: UserId): Promise<User | undefined> {
    if (this.db) {
      return await this.cloud.getUser(userId) // can be undefined
    }
    return undefined
  }

  async deleteUser(userId: UserId) {
    if (this.db) {
      //.
      // await this.db.deleteNode('welcome') //. part of tripplanner - call plugin:deleteUser?
      // await this.deleteNode('welcome') //. part of tripplanner - call plugin:deleteUser?
      await this.db.deleteUser(userId)
    }
  }


  //----------------------------------------------------------------------
  // signin
  //----------------------------------------------------------------------

  // handle change in user login state.
  // user is a user object if being logged in or null.
  // will get user data from cloud db, convert from anonymous user, or make new anonymous user.
  async handleUserChange(user: User) {
    console.log('handleuserchange', user)
    this.setInitializing(true)
    // is a user logged in?
    if (user) {
      // yes
      console.log('usertoken exists - do signin to check if in db etc')
      // check if user in db
      const dbUser = await this.getCloudUser(user.id) //. better as this.cloud.getUser(userId) ?
      if (dbUser) {
        await this.signinExistingUser(user)
      } else {
        await this.signinNewUser(user)
      }
    } else {
      // no-one logged in - make an anonymous user
      await this.signinAnonymousUser()
    }
    // once we know if we're logged in or not we can render more of the ui (eg the signin menu)
    console.log('set initializing off - this will trigger ui rendering')
    this.setInitializing(false)
  }


  async signinAnonymousUser() {
    console.log('signinanon')
    this.db = this.local // switch to local db
    this.setCurrentUserId() // none
    // whatever data is in the cache and local db should stay there
    const anonNodes = await this.local.getNodes() // get anon user's data, if any
    console.log('anonnodes', anonNodes)
    this.initializeCache() // adds empty welcome node
    this.addToCache(anonNodes) // mixin anon user's data - //. what about welcome.childIds?
    await this.saveCache() // save user data back to db
  }

  async signinNewUser(user: User) {
    this.db = this.cloud // switch to cloud db //.
    await this.createUser(user)
    // nm may have data in cache - save it
    console.log('signinnew - user not in db - save nm cache data')
    await this.signinExistingUser(user)
  }

  async signinExistingUser(user: User) {
    // yes - load nm ontology, then app ontology, then user nodes, and set node to welcome
    console.log('signinexisting - user in db - load ontology and nodes', user)
    const anonNodes = await this.local.getNodes() // get anon user's data, if any, or []
    console.log('anonnodes', anonNodes)
    this.db = this.cloud // switch to cloud db //.
    this.setCurrentUserId(user.id)
    this.hydrating = true // this flag will keep the following calls from dumping incoming data to the db
    this.initializeCache() // loads base ontology and any plugin data
    await this.hydrateCache() // load user nodes from db into cache, if any

    const anonWelcome = anonNodes.find(node => node.id === 'welcome')
    if (anonWelcome) {
      const existingWelcome = this.getNode('welcome')
      const childIds = this.mergeChildIds(existingWelcome, anonWelcome)
      existingWelcome.childIds = childIds
      anonWelcome.childIds = childIds
    }

    this.addToCache(anonNodes) // mix in anon user's data

    this.hydrating = false
    if (anonNodes.length > 0) {
      await this.saveCache() // save the mixed in data to db
    }
    await this.local.clearNodes() // clear local data, if any
    return user
  }

  signout() {
    this.signinAnonymousUser()
  }


  mergeChildIds(node1: INode, node2: INode) {
    const childIds1 = node1.childIds
    const childIds2 = node2.childIds
    let childIds
    if (childIds1 && childIds2) {
      childIds = childIds1 + ',' + childIds2
    } else if (childIds1 && !childIds2) {
      childIds = childIds1
    } else if (!childIds1 && childIds2) {
      childIds = childIds2
    } else if (!childIds1 && !childIds2) {
      childIds = undefined
    }
    // if (childIds2) {
    //   if (childIds1) {
    //     childIds1 += ',' + childIds2
    //   } else {
    //     childIds1 = childIds2
    //   }
    // }
    // return childIds1
    return childIds
  }

  //----------------------------------------------------------------------
  // cache
  //----------------------------------------------------------------------

  private initializeCache() {
    this.clearCache()
    this.loadOntology()
    for (let plugin of Object.values(this.plugins)) {
      plugin.loadOntology()
      plugin.loadData()
    }
  }

  // load user nodes from the db into the cache
  async hydrateCache() {
    if (this.db) {
      const nodes = await this.db.getNodes()
      console.log('hydrate cache - get user nodes from db', nodes)
      // fill the cache with nodes (skips system nodes, in case any there)
      for (let node of nodes) {
        this.createNode(node)
      }
      // don't do this because it sets childIds in basically random order - 
      // better to store/hydrate the welcome object with the user objects
      // // handle parentIds
      // // want to make sure trips are set as children of welcome
      // for (let node of nodes) {
      //   if (node.parentId) {
      //     this.addChild(node.id, node.parentId as string)
      //   }
      // }
    }
  }

  // save user nodes to the db
  async saveCache() {
    //. try this.getNodes({system:false or undefined}) ?
    const allNodes = this.getNodes() // get all nodes in the cache
    const userNodes = allNodes.filter(node => !node.system) // ditch system nodes
    if (this.db) {
      await this.db.saveNodes(userNodes)
    }
  }

  private setCacheNode(nodeId: NodeId, node: INode): void {
    this.cache[nodeId] = node
  }

  // throws an error if node not found, to prevent further errors
  private getCacheNode(nodeId: NodeId): INode {
    const node = this.cache[nodeId] // returns undefined if not there
    if (node === undefined) {
      throw new Error(`Node not found in cache: '${nodeId}'`)
    }
    return node
  }

  private getCacheNodes(): INode[] {
    return Object.values(this.cache)
  }

  // this needs to be public for mobx to decorate it
  deleteCacheNode(nodeId: NodeId): void {
    console.log('deletecachenode', nodeId)
    delete this.cache[nodeId]
    console.assert(this.cache[nodeId] === undefined)
    console.log(this.cache[nodeId]) // undefined
  }

  clearCache(): void {
    this.cache = {}
  }

  addToCache(nodes: Props[]) {
    for (let node of nodes) {
      // console.log('addtocache node', node.id, this.initializing)
      this.createNode(node)
    }
    // handle parentIds
    // mainly want to make sure trips are set as children of welcome
    //. better to have a subscription/plugin type thing - call for all relns
    for (let node of nodes) {
      if (node.parentId) {
        this.addChild(node.id, node.parentId)
      }
    }
  }

  loadOntology(): void {
    this.addToCache(neomemOntology)
  }


  //----------------------------------------------------------------------
  // import/export
  //----------------------------------------------------------------------

  // get all data in cache/db as string
  // see also getTreeText in parent-child relns
  getText(): string {
    return ''
  }

  //. this would replace all data in db?
  setText(text: string) {
  }

  //. parse text and add nodes
  importText(text: string) {
  }


  //----------------------------------------------------------------------
  // nodes
  //----------------------------------------------------------------------

  // make a node object and add it to the store
  // pass optional {parentId} for the parentchild relation
  //. uhh, or do that in separate step, so can handle loading a bunch of nodes at once
  createNode(props: Props = {}): INode {

    // adds an id if not specified
    const node = new Node(props)

    // save node to cache
    this.setCacheNode(node.id, node)

    // // add node to parent's children collection, if parentId specified
    // //. should walk through available relations, check for parts, etc

    // //. what if parent doesnt exist yet, as when loading a bunch of stuff from database? then bombs!
    // //  that's an argument for just keeping id's in neomem instead of actual refs
    // //  until we switch to a better cache system
    // if (node.parentId) {
    //   this.addChild(node.id, node.parentId)
    // }

    // notify subscribers
    // this.notify('add', node.id, node) //?
    // this.notify('create', node)
    if (!this.hydrating && !node.system) {
      if (this.db) {
        this.db.createNode(node)
      }
    }

    return node
  }


  //----------------------------------------------------------------------
  // queries
  //----------------------------------------------------------------------

  getNode(nodeId: NodeId): INode {
    // console.log('getnode', nodeId)
    return this.getCacheNode(nodeId) // throws error if not there
  }

  // find nodes that match query shape - all values must match
  // warning: results are unordered - if want ordered children use getChildren
  getNodes(pattern: Pattern = {}): INode[] {
    const nodes = this.getCacheNodes()
    const matches = nodes.filter(node => {
      return Object.keys(pattern).every(key => pattern[key] === node[key]) // AND all matches together
    })
    return matches
  }


  //----------------------------------------------------------------------
  // node values
  //----------------------------------------------------------------------

  //. bombs if node not there
  setNodeValue(nodeId: NodeId, fieldId: string, fieldValue: any): void {
    // console.log('setnodevalue', nodeId, fieldId, fieldValue)
    //. needed to do this for mobx to observe change?
    // this.cache[nodeId][fieldId] = fieldValue
    // const node = this.cache[nodeId]
    const node = this.getCacheNode(nodeId) // will throw error if not there
    node[fieldId] = fieldValue
    // tell db
    if (!this.hydrating && !node.system) {
      if (this.db) {
        this.db.setNodeValue(nodeId, fieldId, fieldValue)
      }
    }
  }

  //. bombs if node not there
  // will return undefined if field not there
  getNodeValue(nodeId: NodeId, fieldId: string): NodeValue {
    // return this.cache[nodeId][fieldId]
    const node = this.getCacheNode(nodeId) // will throw error if not there
    return node[fieldId]
  }

  //. bombs if node not there
  deleteNodeValue(nodeId: NodeId, fieldId: string): void {
    delete this.cache[nodeId][fieldId]
    // tell db
    const node = this.getNode(nodeId)
    if (!this.hydrating && !node.system) {
      if (this.db) {
        this.db.deleteNodeValue(nodeId, fieldId)
      }
    }
  }


  //----------------------------------------------------------------------
  // parent-child relations
  //----------------------------------------------------------------------

  //. need these in this file as declarations
  addChild(nodeId: NodeId, parentId: NodeId) { }
  // deleteNode(nodeId: NodeId) {}
}


//----------------------------------------------------------------------
// mobx decorations
//----------------------------------------------------------------------

decorate(NeomemBase, {

  initializing: observable,
  setInitializing: action,

  cache: observable,
  clearCache: action,
  deleteCacheNode: action,

  currentNodeId: observable,
  setCurrentNodeId: action,

  currentUserId: observable,
  setCurrentUserId: action,

  // values
  setNodeValue: action,
  deleteNodeValue: action,

  // nodes
  createNode: action,
  // deleteNode: action,

})

